/* Native WebGL cortical surface. Anatomy comes from the attributed model asset. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const clamp = (value, low = 0, high = 1) => Math.min(high, Math.max(low, value));
  const mix = (a, b, amount) => a + (b - a) * amount;
  const vertexSource = `
    attribute vec3 aPosition;
    attribute vec3 aNormal;
    attribute float aRegion;
    attribute float aSulc;
    attribute float aPfc;
    uniform mat4 uModel;
    uniform mat4 uViewProjection;
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec4 vRegion;
    varying vec2 vDeepAnatomy;
    varying float vSulc;
    varying float vPfc;
    varying float vLocalY;
    void main() {
      vec4 position = uModel * vec4(aPosition, 1.0);
      vPosition = position.xyz;
      vNormal = mat3(uModel) * aNormal;
      vRegion = vec4(
        1.0 - step(0.45, abs(aRegion - 1.0)),
        1.0 - step(0.45, abs(aRegion - 2.0)),
        1.0 - step(0.45, abs(aRegion - 3.0)),
        1.0 - step(0.45, abs(aRegion - 4.0))
      );
      vSulc = aSulc;
      vPfc = aPfc;
      vLocalY = aPosition.y;
      vDeepAnatomy = vec2(1.0 - step(0.45, abs(aRegion - 5.0)), 1.0 - step(0.45, abs(aRegion - 6.0)));
      gl_Position = uViewProjection * position;
    }
  `;
  const fragmentSource = `
    precision mediump float;
    uniform vec3 uCamera;
    uniform vec4 uRegions;
    uniform float uBloomPass;
    uniform float uMaterial;
    uniform float uOpacity;
    uniform vec3 uAccent;
    uniform float uScalpCut;
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec4 vRegion;
    varying vec2 vDeepAnatomy;
    varying float vSulc;
    varying float vPfc;
    varying float vLocalY;
    void main() {
      vec3 normal = normalize(vNormal);
      if (!gl_FrontFacing) normal *= -1.0;
      vec3 view = normalize(uCamera - vPosition);
      vec3 key = normalize(vec3(-2.8, 4.1, 4.2));
      vec3 fill = normalize(vec3(3.0, 1.0, 1.5));
      float diffuse = max(dot(normal, key), 0.0);
      float fillLight = max(dot(normal, fill), 0.0);
      float rim = pow(1.0 - max(dot(normal, view), 0.0), 3.1);
      if (uMaterial > 0.5) {
        if (uMaterial < 1.5 && vLocalY < uScalpCut) discard;
        if (uMaterial < 1.5) {
          float glassRim = pow(1.0 - max(dot(normal, view), 0.0), 2.0);
          float reflection = pow(max(dot(normal, normalize(key + view)), 0.0), 64.0);
          vec3 glass = vec3(0.035, 0.12, 0.13) + glassRim * vec3(0.37, 0.92, 0.86) * 1.35 + reflection * vec3(0.65, 0.95, 1.0);
          float alpha = clamp(uOpacity * (0.42 + glassRim * 2.5 + reflection * 1.8), 0.0, 0.76);
          if (uBloomPass > 0.5) glass = glassRim * vec3(0.075, 0.25, 0.22);
          gl_FragColor = vec4(glass, alpha);
          return;
        }
        vec3 base = uMaterial < 1.5 ? vec3(0.24, 0.28, 0.27) : vec3(0.018, 0.025, 0.025);
        float weave = sin(vPosition.x * 360.0) * sin(vPosition.y * 360.0) * 0.012;
        vec3 surface = base * (0.30 + diffuse * 0.95 + fillLight * 0.28);
        surface += rim * vec3(0.035, 0.085, 0.075);
        if (uMaterial > 1.5 && uMaterial < 2.5) surface += weave + pow(max(dot(normal, normalize(key + view)), 0.0), 36.0) * 0.035;
        if (uMaterial > 2.5 && uMaterial < 3.5) surface = uAccent * (0.55 + diffuse * 0.35 + rim * 0.4);
        if (uMaterial > 3.5) surface = vec3(0.012, 0.018, 0.019) * (0.4 + diffuse) + pow(max(dot(normal, normalize(key + view)), 0.0), 72.0) * vec3(0.27, 0.35, 0.37) + rim * vec3(0.025, 0.04, 0.04);
        if (uBloomPass > 0.5) surface = uMaterial > 2.5 && uMaterial < 3.5 ? uAccent * 1.4 : vec3(0.0);
        gl_FragColor = vec4(surface, uOpacity);
        return;
      }
      float fold = clamp(0.88 - vSulc * 0.24, 0.56, 1.0);
      vec3 tissue = mix(vec3(0.53, 0.48, 0.39), vec3(0.94, 0.91, 0.81), fold);
      tissue = mix(tissue, vec3(0.79, 0.73, 0.64), vDeepAnatomy.x * 0.44);
      tissue = mix(tissue, vec3(0.82, 0.79, 0.73), vDeepAnatomy.y * 0.36);
      float tinyTexture = sin(vPosition.x * 91.0 + vPosition.y * 33.0) * sin(vPosition.z * 77.0) * 0.012;
      tissue += tinyTexture;
      vec3 light = vec3(0.13, 0.15, 0.16) + diffuse * vec3(0.92, 0.86, 0.72) + fillLight * vec3(0.18, 0.23, 0.26);
      vec3 halfVector = normalize(key + view);
      float specular = pow(max(dot(normal, halfVector), 0.0), 45.0) * 0.14;
      vec4 activation = vRegion * uRegions;
      float active = clamp(dot(activation, vec4(1.0)), 0.0, 1.0);
      vec3 colour = activation.x * vec3(0.70, 1.0, 0.06)
        + activation.y * vec3(0.03, 0.93, 0.91)
        + activation.z * vec3(0.24, 0.50, 1.0)
        + activation.w * vec3(1.0, 0.61, 0.15);
      vec3 emission = colour * (0.27 + diffuse * 0.15 + rim * 0.36) * fold;
      emission += vec3(0.52, 0.78, 0.035) * vPfc * activation.x * 0.06;
      if (uBloomPass > 0.5) {
        gl_FragColor = vec4(emission * 1.35 + vec3(0.035, 0.07, 0.045) * rim, uOpacity);
      } else {
        vec3 surface = tissue * light * fold;
        surface = mix(surface, surface * (0.58 + colour * 0.78), active * 0.64);
        surface += emission + vec3(specular) + rim * vec3(0.07, 0.12, 0.12);
        surface = surface / (surface + vec3(0.48));
        surface = pow(surface, vec3(0.91));
        gl_FragColor = vec4(surface, uOpacity);
      }
    }
  `;
  const quadVertexSource = `
    attribute vec2 aPoint;
    varying vec2 vUv;
    void main() {
      vUv = aPoint * 0.5 + 0.5;
      gl_Position = vec4(aPoint, 0.0, 1.0);
    }
  `;
  const blurSource = `
    precision mediump float;
    uniform sampler2D uTexture;
    uniform vec2 uDirection;
    uniform float uComposite;
    varying vec2 vUv;
    void main() {
      vec3 colour = texture2D(uTexture, vUv).rgb * 0.227027;
      colour += texture2D(uTexture, vUv + uDirection * 1.384615).rgb * 0.316216;
      colour += texture2D(uTexture, vUv - uDirection * 1.384615).rgb * 0.316216;
      colour += texture2D(uTexture, vUv + uDirection * 3.230769).rgb * 0.070270;
      colour += texture2D(uTexture, vUv - uDirection * 3.230769).rgb * 0.070270;
      float luminance = max(colour.r, max(colour.g, colour.b));
      gl_FragColor = vec4(colour * (uComposite > 0.5 ? 0.43 : 1.0), uComposite > 0.5 ? clamp(luminance * 1.8, 0.0, 0.68) : 1.0);
    }
  `;
  const identity = () => new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  const multiply = (left, right) => {
    const result = new Float32Array(16);
    for (let column = 0; column < 4; column += 1) {
      for (let row = 0; row < 4; row += 1) {
        for (let index = 0; index < 4; index += 1) result[column * 4 + row] += left[index * 4 + row] * right[column * 4 + index];
      }
    }
    return result;
  };
  const rotation = (yaw, tilt) => {
    const cy = Math.cos(yaw); const sy = Math.sin(yaw);
    const cx = Math.cos(tilt); const sx = Math.sin(tilt);
    const y = new Float32Array([cy, 0, -sy, 0, 0, 1, 0, 0, sy, 0, cy, 0, 0, 0, 0, 1]);
    const x = new Float32Array([1, 0, 0, 0, 0, cx, sx, 0, 0, -sx, cx, 0, 0, 0, 0, 1]);
    return multiply(x, y);
  };
  const perspective = (field, aspect, near, far) => {
    const f = 1 / Math.tan(field / 2);
    const range = 1 / (near - far);
    return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (near + far) * range, -1, 0, 0, 2 * near * far * range, 0]);
  };
  const transformPoint = (matrix, point) => {
    const [x, y, z] = point;
    return [matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12], matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13], matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14], matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15]];
  };
  const computedNormals = (positions, indices) => {
    const normals = new Float32Array(positions.length);
    for (let index = 0; index < indices.length; index += 3) {
      const a = indices[index] * 3; const b = indices[index + 1] * 3; const c = indices[index + 2] * 3;
      const ab = [positions[b] - positions[a], positions[b + 1] - positions[a + 1], positions[b + 2] - positions[a + 2]];
      const ac = [positions[c] - positions[a], positions[c + 1] - positions[a + 1], positions[c + 2] - positions[a + 2]];
      const n = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
      [a, b, c].forEach((offset) => { normals[offset] += n[0]; normals[offset + 1] += n[1]; normals[offset + 2] += n[2]; });
    }
    for (let index = 0; index < normals.length; index += 3) {
      const length = Math.hypot(normals[index], normals[index + 1], normals[index + 2]) || 1;
      normals[index] /= length; normals[index + 1] /= length; normals[index + 2] /= length;
    }
    return normals;
  };
  const surfaceOccludes = (model, eye, anchor) => {
    const dx = anchor[0] - eye[0]; const dy = anchor[1] - eye[1]; const dz = anchor[2] - eye[2];
    const distance = Math.hypot(dx, dy, dz);
    const rx = dx / distance; const ry = dy / distance; const rz = dz / distance;
    const positions = model.positions; const faces = model.indices;
    // Exclude the anchor's own surface intersection while checking both triangle faces.
    const limit = distance - 0.012;
    for (let index = 0; index < faces.length; index += 3) {
      const a = faces[index] * 3; const b = faces[index + 1] * 3; const c = faces[index + 2] * 3;
      const e1x = positions[b] - positions[a]; const e1y = positions[b + 1] - positions[a + 1]; const e1z = positions[b + 2] - positions[a + 2];
      const e2x = positions[c] - positions[a]; const e2y = positions[c + 1] - positions[a + 1]; const e2z = positions[c + 2] - positions[a + 2];
      const px = ry * e2z - rz * e2y; const py = rz * e2x - rx * e2z; const pz = rx * e2y - ry * e2x;
      const determinant = e1x * px + e1y * py + e1z * pz;
      if (Math.abs(determinant) < 0.0000001) continue;
      const inverse = 1 / determinant;
      const tx = eye[0] - positions[a]; const ty = eye[1] - positions[a + 1]; const tz = eye[2] - positions[a + 2];
      const u = (tx * px + ty * py + tz * pz) * inverse;
      if (u < 0 || u > 1) continue;
      const qx = ty * e1z - tz * e1y; const qy = tz * e1x - tx * e1z; const qz = tx * e1y - ty * e1x;
      const v = (rx * qx + ry * qy + rz * qz) * inverse;
      if (v < 0 || u + v > 1) continue;
      const hit = (e2x * qx + e2y * qy + e2z * qz) * inverse;
      if (hit > 0.0001 && hit < limit) return true;
    }
    return false;
  };
  const prepareModel = (source, sharedFit) => {
    if (!source || !Array.isArray(source.positions) || !Array.isArray(source.indices) || !Array.isArray(source.regions)) throw new Error('Invalid cortical model');
    const vertices = source.positions.length / 3;
    if (!Number.isInteger(vertices) || vertices < 100 || vertices > 65535 || source.regions.length !== vertices || source.indices.length % 3) throw new Error('Unsupported cortical model');
    const low = [Infinity, Infinity, Infinity]; const high = [-Infinity, -Infinity, -Infinity];
    source.positions.forEach((value, index) => {
      if (!Number.isFinite(value)) throw new Error('Invalid model coordinate');
      const axis = index % 3; low[axis] = Math.min(low[axis], value); high[axis] = Math.max(high[axis], value);
    });
    const centre = sharedFit ? sharedFit.centre : low.map((value, axis) => (value + high[axis]) / 2);
    const scale = sharedFit ? sharedFit.scale : 2.5 / Math.max(...high.map((value, axis) => value - low[axis]));
    const convertPoint = (point) => [(point[0] - centre[0]) * scale, (point[2] - centre[2]) * scale, -(point[1] - centre[1]) * scale];
    const positions = new Float32Array(source.positions.length);
    for (let index = 0; index < vertices; index += 1) positions.set(convertPoint(source.positions.slice(index * 3, index * 3 + 3)), index * 3);
    source.indices.forEach((value) => { if (!Number.isInteger(value) || value < 0 || value >= vertices) throw new Error('Invalid model face'); });
    const indices = new Uint16Array(source.indices);
    let normals;
    if (Array.isArray(source.normals) && source.normals.length === positions.length) {
      normals = new Float32Array(source.normals.length);
      for (let index = 0; index < vertices; index += 1) {
        const offset = index * 3; const n = source.normals.slice(offset, offset + 3);
        if (!n.every(Number.isFinite)) throw new Error('Invalid model normal');
        const length = Math.hypot(...n) || 1;
        normals.set([n[0] / length, n[2] / length, -n[1] / length], offset);
      }
    } else normals = computedNormals(positions, indices);
    const regions = new Float32Array(source.regions.map((value) => {
      if (!Number.isInteger(value) || value < 0 || value > 6) throw new Error('Invalid anatomical region');
      return value;
    }));
    const sulc = new Float32Array(vertices);
    if (Array.isArray(source.sulc) && source.sulc.length === vertices) source.sulc.forEach((value, index) => { if (!Number.isFinite(value)) throw new Error('Invalid sulcal depth'); sulc[index] = clamp(value, -1, 1); });
    const pfc = new Float32Array(vertices);
    if (Array.isArray(source.pfc) && source.pfc.length === vertices) source.pfc.forEach((value, index) => { if (!Number.isFinite(value)) throw new Error('Invalid prefrontal mask'); pfc[index] = clamp(value); });
    let radius = 0;
    for (let index = 0; index < positions.length; index += 3) radius = Math.max(radius, Math.hypot(positions[index], positions[index + 1], positions[index + 2]));
    const centroids = {};
    for (let region = 1; region <= 6; region += 1) {
      let count = 0; const point = [0, 0, 0]; const normal = [0, 0, 0];
      for (let vertex = 0; vertex < vertices; vertex += 1) {
        if (regions[vertex] !== region) continue;
        count += 1;
        for (let axis = 0; axis < 3; axis += 1) { point[axis] += positions[vertex * 3 + axis]; normal[axis] += normals[vertex * 3 + axis]; }
      }
      if (count) centroids[region] = { point: point.map((value) => value / count), normal };
    }
    const anchors = {};
    const sourceAnchors = source.centroids || {};
    const anchorVertices = source.metadata && source.metadata.anchorVertexIndices || {};
    const anchorRegions = { frontal: 1, pfc: 1, parietal: 2, temporal: 3, occipital: 4, cerebellum: 5, brainstem: 6 };
    Object.keys(anchorRegions).forEach((region) => {
      anchors[region] = ['left', 'right'].map((side) => {
        const key = `${region}_${side}`;
        const point = sourceAnchors[key];
        if (!Array.isArray(point) || point.length !== 3) return null;
        let vertex = anchorVertices[key];
        if (!Number.isInteger(vertex)) {
          let minimumDistance = Infinity;
          source.positions.forEach((_, offset) => {
            if (offset % 3 || regions[offset / 3] !== anchorRegions[region]) return;
            const distance = Math.hypot(source.positions[offset] - point[0], source.positions[offset + 1] - point[1], source.positions[offset + 2] - point[2]);
            if (distance < minimumDistance) { minimumDistance = distance; vertex = offset / 3; }
          });
        }
        const normal = Number.isInteger(vertex) && vertex >= 0 && vertex < vertices ? Array.from(normals.slice(vertex * 3, vertex * 3 + 3)) : null;
        return { point: convertPoint(point), normal, side };
      }).filter(Boolean);
    });
    const fitPoints = [];
    const stride = Math.max(1, Math.ceil(vertices / 1200));
    for (let vertex = 0; vertex < vertices; vertex += stride) fitPoints.push(Array.from(positions.slice(vertex * 3, vertex * 3 + 3)));
    return { positions, normals, regions, sulc, pfc, indices, radius, centroids, anchors, fit: { centre, scale }, convertPoint, fitPoints };
  };
  const unit = (vector) => { const length = Math.hypot(...vector) || 1; return vector.map((value) => value / length); };
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const geometry = (points, faces) => {
    const positions = new Float32Array(points.flat()); const indices = new Uint16Array(faces);
    const count = points.length;
    return { positions, indices, normals: computedNormals(positions, indices), regions: new Float32Array(count), sulc: new Float32Array(count), pfc: new Float32Array(count) };
  };
  const contactGeometry = (point, normal, radius = 0.029) => {
    const up = Math.abs(normal[1]) > 0.8 ? [1, 0, 0] : [0, 1, 0];
    const first = unit(cross(normal, up)); const second = unit(cross(normal, first));
    const centre = point.map((value, axis) => value + normal[axis] * 0.044);
    const points = []; const faces = []; const around = 20; const sides = 6;
    for (let ring = 0; ring < around; ring += 1) {
      const angle = ring / around * Math.PI * 2;
      const radial = first.map((value, axis) => value * Math.cos(angle) + second[axis] * Math.sin(angle));
      for (let side = 0; side < sides; side += 1) {
        const bend = side / sides * Math.PI * 2;
        points.push(centre.map((value, axis) => value + radial[axis] * (radius + Math.cos(bend) * 0.008) + normal[axis] * Math.sin(bend) * 0.008));
      }
    }
    for (let ring = 0; ring < around; ring += 1) for (let side = 0; side < sides; side += 1) {
      const a = ring * sides + side; const b = (ring + 1) % around * sides + side; const c = (ring + 1) % around * sides + (side + 1) % sides; const d = ring * sides + (side + 1) % sides;
      faces.push(a, b, d, b, c, d);
    }
    return { mesh: geometry(points, faces), point: centre, normal };
  };
  const bandGeometry = (path, normals) => {
    const points = []; const faces = [];
    path.forEach((centre, index) => {
      const normal = normals[index];
      const previous = path[(index + path.length - 1) % path.length]; const next = path[(index + 1) % path.length];
      const tangent = unit(next.map((value, axis) => value - previous[axis]));
      let vertical = unit(cross(tangent, normal));
      if (vertical[1] < 0) vertical = vertical.map((value) => -value);
      const front = clamp((-centre[2] + 0.25) / 1.1);
      const halfWidth = 0.11 + front * 0.085;
      [[-1, 0.019], [1, 0.019], [-1, 0.042], [1, 0.042]].forEach(([side, lift]) => points.push(centre.map((value, axis) => value + vertical[axis] * halfWidth * side + normal[axis] * lift)));
    });
    for (let index = 0; index < path.length; index += 1) {
      const a = index * 4; const b = (index + 1) % path.length * 4;
      [[0, 1], [2, 3], [0, 2], [1, 3]].forEach(([first, second]) => faces.push(a + first, b + first, a + second, b + first, b + second, a + second));
    }
    return geometry(points, faces);
  };
  const frontPodGeometry = (point, normal) => {
    const horizontal = unit(cross(normal, [0, 1, 0])); const vertical = unit(cross(horizontal, normal));
    const points = []; const faces = []; const ringCount = 24;
    const rings = [[0.038, 0.46, 0.19, 0.085], [0.079, 0.475, 0.195, 0.09], [0.122, 0.45, 0.175, 0.08]];
    rings.forEach(([lift, halfWidth, halfHeight, radius]) => {
      for (let corner = 0; corner < 4; corner += 1) {
        const cx = (corner === 0 || corner === 3 ? 1 : -1) * (halfWidth - radius);
        const cy = (corner < 2 ? 1 : -1) * (halfHeight - radius);
        for (let segment = 0; segment < 6; segment += 1) {
          const angle = corner * Math.PI / 2 + segment / 5 * Math.PI / 2;
          const x = cx + Math.cos(angle) * radius; const y = cy + Math.sin(angle) * radius;
          points.push(point.map((value, axis) => value + horizontal[axis] * x + vertical[axis] * y + normal[axis] * lift));
        }
      }
    });
    for (let ring = 0; ring < rings.length - 1; ring += 1) for (let edge = 0; edge < ringCount; edge += 1) {
      const next = (edge + 1) % ringCount; const a = ring * ringCount + edge; const b = ring * ringCount + next; const c = (ring + 1) * ringCount + edge; const d = (ring + 1) * ringCount + next;
      faces.push(a, b, c, b, d, c);
    }
    const backCentre = points.length; points.push(point.map((value, axis) => value + normal[axis] * rings[0][0]));
    const frontCentre = points.length; points.push(point.map((value, axis) => value + normal[axis] * rings[2][0]));
    for (let edge = 0; edge < ringCount; edge += 1) { const next = (edge + 1) % ringCount; faces.push(backCentre, edge, next, frontCentre, ringCount * 2 + next, ringCount * 2 + edge); }
    return geometry(points, faces);
  };
  const prepareScalp = (source, brain) => {
    const count = source.positions && source.positions.length / 3;
    const scalp = prepareModel({ ...source, regions: Array(count).fill(0) }, brain.fit);
    const normalization = source.metadata && source.metadata.normalization;
    if (normalization && normalization.centerRASmm && Number.isFinite(normalization.uniformScale)) {
      const cutZ = (-85 - normalization.centerRASmm[2]) * normalization.uniformScale;
      scalp.clipY = brain.convertPoint([0, 0, cutZ])[1];
      scalp.fitPoints = scalp.fitPoints.filter((point) => point[1] >= scalp.clipY);
    }
    const sourceContacts = source.sensorPositions || source.sensors || source.contacts || {};
    const contacts = {};
    Object.entries(sourceContacts).forEach(([name, supplied]) => {
      const sourcePoint = Array.isArray(supplied) ? supplied : supplied.position || supplied.point;
      if (!Array.isArray(sourcePoint) || sourcePoint.length !== 3 || !sourcePoint.every(Number.isFinite)) return;
      const point = brain.convertPoint(sourcePoint);
      let nearest = 0; let distance = Infinity;
      for (let vertex = 0; vertex < count; vertex += 1) {
        const offset = vertex * 3;
        const next = Math.hypot(scalp.positions[offset] - point[0], scalp.positions[offset + 1] - point[1], scalp.positions[offset + 2] - point[2]);
        if (next < distance) { distance = next; nearest = vertex; }
      }
      const sourceNormal = !Array.isArray(supplied) && supplied.normal;
      const normal = sourceNormal && sourceNormal.length === 3 ? unit([sourceNormal[0], sourceNormal[2], -sourceNormal[1]]) : Array.from(scalp.normals.slice(nearest * 3, nearest * 3 + 3));
      const displayPoint = /optical/i.test(name) || name === 'FPz' ? point.map((value, axis) => value + normal[axis] * 0.12) : point;
      contacts[name] = { ...contactGeometry(displayPoint, normal, /optical/i.test(name) ? 0.023 : 0.029), skinPoint: point, kind: /optical/i.test(name) ? 'optical' : name === 'FPz' ? 'reference' : 'eeg' };
    });
    const sourcePath = source.bandPath || source.fittedBandPath;
    let band;
    if (Array.isArray(sourcePath) && sourcePath.length > 8) {
      const path = sourcePath.map((entry) => brain.convertPoint(Array.isArray(entry) ? entry : entry.position));
      const normals = path.map((point, index) => {
        if (Array.isArray(source.bandPathNormals) && Array.isArray(source.bandPathNormals[index])) {
          const n = source.bandPathNormals[index]; return unit([n[0], n[2], -n[1]]);
        }
        let nearest = 0; let distance = Infinity;
        for (let vertex = 0; vertex < count; vertex += 1) {
          const offset = vertex * 3; const next = Math.hypot(scalp.positions[offset] - point[0], scalp.positions[offset + 1] - point[1], scalp.positions[offset + 2] - point[2]);
          if (next < distance) { distance = next; nearest = vertex; }
        }
        return Array.from(scalp.normals.slice(nearest * 3, nearest * 3 + 3));
      });
      band = bandGeometry(path, normals);
    }
    const pod = contacts.FPz ? frontPodGeometry(contacts.FPz.skinPoint, contacts.FPz.normal) : null;
    return { ...scalp, contacts, band, pod };
  };
  const initialise = (wrapper) => {
    if (instances.has(wrapper)) return;
    const canvas = wrapper.querySelector('canvas');
    const root = wrapper.closest('[data-ih-brain-scroll]');
    const url = wrapper.dataset.modelUrl;
    if (!canvas || !root || !url) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const resources = { buffers: [], programs: [], targets: [] };
    let gl; let model; let scalp; let shader; let quadShader; let quadBuffer; let frame = 0;
    let targets = []; let visible = false; let fetched = false; let disposed = false; let lost = false;
    let width = 0; let height = 0; let bloomWidth = 0; let bloomHeight = 0;
    let currentYaw = 2.30; let targetYaw = 2.30; let currentTilt = 0.12; let targetTilt = 0.12;
    let currentRegions = [0.17, 0.17, 0.17, 0.17]; let targetRegions = currentRegions.slice();
    let currentScalp = 0; let targetScalp = 0; let sensorFocus = 'front';
    let currentDistance = NaN; let currentAimY = NaN; let viewWidth = 0.62; let viewCentreX = -0.36; let fitTargetHeight = 0.82;
    let lastBoundsKey = '';
    let lastTime = 0; let viewProjection = identity(); let camera = [0, 0, 5];
    let restoreCount = 0;
    let labelOcclusionKey = '';
    const labelOcclusion = new Map();
    wrapper.dataset.state = 'waiting';
    const setState = (state) => { wrapper.dataset.state = state; canvas.dataset.state = state; };
    const stop = () => { if (frame) window.cancelAnimationFrame(frame); frame = 0; lastTime = 0; };
    const fail = () => { stop(); setState('fallback'); wrapper.querySelectorAll('[data-brain-label-region], [data-sensor-label]').forEach((label) => { label.hidden = true; }); };
    const compile = (type, code) => {
      const item = gl.createShader(type);
      gl.shaderSource(item, code); gl.compileShader(item);
      if (!gl.getShaderParameter(item, gl.COMPILE_STATUS)) { gl.deleteShader(item); throw new Error('Brain shader compilation failed'); }
      return item;
    };
    const program = (vertex, fragment, attributes, uniforms) => {
      const first = compile(gl.VERTEX_SHADER, vertex); const second = compile(gl.FRAGMENT_SHADER, fragment);
      const item = gl.createProgram(); gl.attachShader(item, first); gl.attachShader(item, second); gl.linkProgram(item);
      gl.deleteShader(first); gl.deleteShader(second);
      if (!gl.getProgramParameter(item, gl.LINK_STATUS)) { gl.deleteProgram(item); throw new Error('Brain shader linking failed'); }
      resources.programs.push(item);
      return { item, attributes: Object.fromEntries(attributes.map((name) => [name, gl.getAttribLocation(item, name)])), uniforms: Object.fromEntries(uniforms.map((name) => [name, gl.getUniformLocation(item, name)])) };
    };
    const buffer = (target, data) => {
      const item = gl.createBuffer(); resources.buffers.push(item);
      gl.bindBuffer(target, item); gl.bufferData(target, data, gl.STATIC_DRAW); return item;
    };
    const destroyTargets = () => {
      targets.forEach((target) => { gl.deleteTexture(target.texture); gl.deleteFramebuffer(target.framebuffer); gl.deleteRenderbuffer(target.depth); });
      targets = []; resources.targets = targets;
    };
    const makeTarget = () => {
      const texture = gl.createTexture(); const framebuffer = gl.createFramebuffer(); const depth = gl.createRenderbuffer();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, bloomWidth, bloomHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer); gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
      gl.bindRenderbuffer(gl.RENDERBUFFER, depth); gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, bloomWidth, bloomHeight);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depth);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) { gl.deleteTexture(texture); gl.deleteFramebuffer(framebuffer); gl.deleteRenderbuffer(depth); throw new Error('Brain framebuffer unavailable'); }
      const target = { texture, framebuffer, depth }; targets.push(target); resources.targets = targets; return target;
    };
    const resize = () => {
      if (!gl || lost || disposed) return;
      const box = canvas.getBoundingClientRect();
      const cap = window.matchMedia('(max-width: 760px)').matches ? 1.25 : 1.5;
      const ratio = Math.min(window.devicePixelRatio || 1, cap);
      const nextWidth = Math.max(1, Math.round(box.width * ratio)); const nextHeight = Math.max(1, Math.round(box.height * ratio));
      if (nextWidth === width && nextHeight === height) return;
      width = nextWidth; height = nextHeight; canvas.width = width; canvas.height = height;
      bloomWidth = Math.max(64, Math.round(width * 0.42)); bloomHeight = Math.max(64, Math.round(height * 0.42));
      destroyTargets(); makeTarget(); makeTarget();
      const mobile = window.matchMedia('(max-width: 760px)').matches;
      const styles = window.getComputedStyle(wrapper);
      viewWidth = clamp(parseFloat(styles.getPropertyValue('--ih-3d-view-width')) || (mobile ? 1 : 0.62), 0.35, 1);
      const centre = parseFloat(styles.getPropertyValue('--ih-3d-center-x'));
      viewCentreX = Number.isFinite(centre) ? clamp(centre, -0.65, 0.65) : mobile ? 0 : -0.36;
      fitTargetHeight = clamp(parseFloat(styles.getPropertyValue('--ih-3d-target-height')) || 0.82, 0.5, 0.94);
      canvas.dataset.viewCenterX = String(viewCentreX);
      canvas.dataset.viewWidth = String(viewWidth);
      canvas.dataset.fitTargetHeight = String(fitTargetHeight);
    };
    const fitView = (matrix, amount) => {
      const points = scalp && currentScalp > 0.18 ? scalp.fitPoints : model.fitPoints;
      const transformed = points.map((point) => transformPoint(matrix, point));
      let minimumY = Infinity; let maximumY = -Infinity;
      transformed.forEach((point) => { minimumY = Math.min(minimumY, point[1]); maximumY = Math.max(maximumY, point[1]); });
      const aimY = (minimumY + maximumY) / 2;
      const field = 36 * Math.PI / 180; const focal = 1 / Math.tan(field / 2); const aspect = width / height;
      let distance = 2;
      transformed.forEach((point) => {
        distance = Math.max(distance, point[2] + Math.abs(point[1] - aimY) * focal / fitTargetHeight, point[2] + Math.abs(point[0]) * focal / (aspect * viewWidth * 0.90));
      });
      distance += 0.08;
      currentDistance = Number.isFinite(currentDistance) ? mix(currentDistance, distance, amount) : distance;
      currentAimY = Number.isFinite(currentAimY) ? mix(currentAimY, aimY, amount) : aimY;
      camera = [0, currentAimY, currentDistance];
      const view = identity(); view[13] = -currentAimY; view[14] = -currentDistance;
      const projection = perspective(field, aspect, 0.1, 40); projection[8] = -viewCentreX;
      viewProjection = multiply(projection, view);
      return Math.abs(currentDistance - distance) + Math.abs(currentAimY - aimY);
    };
    const updateTargets = () => {
      const region = root.dataset.ihBrainRegionState || 'all';
      const progress = clamp(parseFloat(root.style.getPropertyValue('--ih-brain-progress')) || 0);
      const stepProgress = clamp(parseFloat(root.style.getPropertyValue('--ih-brain-step-progress')) || 0);
      const step = parseInt(root.dataset.ihBrainStepState, 10) || 1;
      const motionStopped = reduced.matches || root.dataset.ihMotion === 'paused';
      const all = region === 'all';
      targetRegions = all ? [0.20, 0.17, 0.16, 0.14] : region === 'front' ? [1, 0.025, 0.025, 0.025] : region === 'sides' ? [0.025, 0.94, 0.94, 0.025] : [0.025, 0.025, 0.025, 1];
      sensorFocus = step === 2 ? 'optical' : stepProgress < 0.28 ? 'front' : stepProgress < 0.52 ? 'left-ear' : stepProgress < 0.76 ? 'right-ear' : 'reference';
      targetScalp = scalp ? step < 3 ? 1 : step === 3 ? 1 - clamp(stepProgress * 3) : 0 : 0;
      if (step < 3 && scalp) targetRegions = [0.035, 0.035, 0.035, 0.035];
      if (root.dataset.ihMotion === 'paused' && !reduced.matches) targetScalp = currentScalp;
      if (!motionStopped) {
        let yaw = all ? mix(2.36, -0.5, progress) : region === 'front' ? 2.28 : region === 'sides' ? 1.38 : 0.34;
        if (targetScalp > 0.5) yaw = sensorFocus === 'left-ear' ? 1.42 : sensorFocus === 'right-ear' ? 4.86 : 2.98;
        targetYaw = currentYaw + Math.atan2(Math.sin(yaw - currentYaw), Math.cos(yaw - currentYaw));
        targetTilt = targetScalp > 0.5 ? 0.05 : region === 'sides' ? 0.25 : region === 'back' ? 0.16 : 0.12;
      } else { targetYaw = currentYaw; targetTilt = currentTilt; }
      canvas.dataset.activeRegion = region;
      canvas.dataset.motion = motionStopped ? 'paused' : 'scroll';
      wrapper.dataset.sensorFocus = sensorFocus; canvas.dataset.sensorFocus = sensorFocus;
    };
    const attributes = (buffers) => {
      Object.entries(buffers).forEach(([name, entry]) => {
        const location = shader.attributes[name]; if (location < 0) return;
        gl.bindBuffer(gl.ARRAY_BUFFER, entry.item); gl.enableVertexAttribArray(location); gl.vertexAttribPointer(location, entry.size, gl.FLOAT, false, 0, 0);
      });
    };
    const uploadMesh = (mesh) => {
      mesh.buffers = { aPosition: { item: buffer(gl.ARRAY_BUFFER, mesh.positions), size: 3 }, aNormal: { item: buffer(gl.ARRAY_BUFFER, mesh.normals), size: 3 }, aRegion: { item: buffer(gl.ARRAY_BUFFER, mesh.regions), size: 1 }, aSulc: { item: buffer(gl.ARRAY_BUFFER, mesh.sulc), size: 1 }, aPfc: { item: buffer(gl.ARRAY_BUFFER, mesh.pfc), size: 1 } };
      mesh.indexBuffer = buffer(gl.ELEMENT_ARRAY_BUFFER, mesh.indices);
    };
    const drawMesh = (mesh, matrix, bloom, material = 0, opacity = 1, accent = [0, 0, 0]) => {
      if (opacity < 0.008) return;
      gl.useProgram(shader.item); gl.enable(gl.DEPTH_TEST); gl.depthMask(true); gl.disable(gl.BLEND); gl.disable(gl.CULL_FACE);
      if (material === 1) { gl.enable(gl.CULL_FACE); gl.cullFace(gl.BACK); }
      if (material === 1) gl.depthMask(false);
      if (opacity < 0.995) { gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA); }
      attributes(mesh.buffers);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, mesh.indexBuffer);
      gl.uniformMatrix4fv(shader.uniforms.uModel, false, matrix); gl.uniformMatrix4fv(shader.uniforms.uViewProjection, false, viewProjection);
      gl.uniform3fv(shader.uniforms.uCamera, camera); gl.uniform4fv(shader.uniforms.uRegions, currentRegions); gl.uniform1f(shader.uniforms.uBloomPass, bloom ? 1 : 0);
      gl.uniform1f(shader.uniforms.uMaterial, material); gl.uniform1f(shader.uniforms.uOpacity, opacity); gl.uniform3fv(shader.uniforms.uAccent, accent);
      gl.uniform1f(shader.uniforms.uScalpCut, material === 1 && Number.isFinite(mesh.clipY) ? mesh.clipY : -1000);
      gl.drawElements(gl.TRIANGLES, mesh.indices.length, gl.UNSIGNED_SHORT, 0);
    };
    const drawScene = (matrix, bloom) => {
      drawMesh(model, matrix, bloom);
      if (!scalp || currentScalp < 0.008) return;
      drawMesh(scalp, matrix, bloom, 1, currentScalp * 0.24);
      if (scalp.band) drawMesh(scalp.band, matrix, bloom, 2, currentScalp);
      if (scalp.pod) drawMesh(scalp.pod, matrix, bloom, 4, currentScalp);
      Object.entries(scalp.contacts).forEach(([name, contact]) => {
        const active = sensorFocus === 'front' ? ['AF7', 'AF8'].includes(name) : sensorFocus === 'left-ear' ? name === 'TP9' : sensorFocus === 'right-ear' ? name === 'TP10' : sensorFocus === 'reference' ? name === 'FPz' : contact.kind === 'optical';
        const brightness = active ? 1 : 0.20;
        const colour = contact.kind === 'optical' ? [1, 0.62, 0.12] : contact.kind === 'reference' ? [0.88, 0.95, 0.92] : [0.06, 0.95, 0.86];
        drawMesh(contact.mesh, matrix, bloom, 3, currentScalp, colour.map((value) => value * brightness));
      });
    };
    const drawQuad = (texture, direction, composite) => {
      gl.useProgram(quadShader.item); gl.disable(gl.DEPTH_TEST); gl.depthMask(false);
      Object.values(shader.attributes).forEach((location) => { if (location >= 0) gl.disableVertexAttribArray(location); });
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer); gl.enableVertexAttribArray(quadShader.attributes.aPoint); gl.vertexAttribPointer(quadShader.attributes.aPoint, 2, gl.FLOAT, false, 0, 0);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, texture); gl.uniform1i(quadShader.uniforms.uTexture, 0);
      gl.uniform2fv(quadShader.uniforms.uDirection, direction); gl.uniform1f(quadShader.uniforms.uComposite, composite ? 1 : 0);
      if (composite) { gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE); } else gl.disable(gl.BLEND);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.disableVertexAttribArray(quadShader.attributes.aPoint); gl.disable(gl.BLEND);
    };
    const updateLabels = (matrix, rotationSettled) => {
      const labels = wrapper.querySelectorAll('[data-brain-label-region]');
      if (!rotationSettled || currentScalp > 0.10) { labels.forEach((label) => { label.hidden = true; }); return; }
      const combined = multiply(viewProjection, matrix);
      // The rotation is orthonormal: its transpose transforms the camera into mesh space.
      const eye = [matrix[0] * camera[0] + matrix[1] * camera[1] + matrix[2] * camera[2], matrix[4] * camera[0] + matrix[5] * camera[1] + matrix[6] * camera[2], matrix[8] * camera[0] + matrix[9] * camera[1] + matrix[10] * camera[2]];
      const occlusionKey = `${currentYaw.toFixed(2)}:${currentTilt.toFixed(2)}:${camera[1].toFixed(2)}:${camera[2].toFixed(2)}`;
      if (occlusionKey !== labelOcclusionKey) { labelOcclusionKey = occlusionKey; labelOcclusion.clear(); }
      const aliases = { frontal: 1, front: 1, parietal: 2, temporal: 3, occipital: 4, back: 4, cerebellum: 5, brainstem: 6 };
      labels.forEach((label) => {
        const name = label.dataset.brainLabelRegion;
        const region = aliases[name] || Number(name);
        const candidates = model.anchors[name] && model.anchors[name].length ? model.anchors[name] : [model.centroids[region]].filter(Boolean);
        const facing = candidates.map((candidate) => {
          const world = transformPoint(matrix, candidate.point);
          const normal = candidate.normal ? transformPoint(matrix, candidate.normal) : null;
          return { candidate, depth: world[2], facing: !normal || normal[2] > 0.08 };
        }).filter((candidate) => candidate.facing).sort((a, b) => b.depth - a.depth);
        const visibleAnchor = facing.find(({ candidate }) => {
          if (!labelOcclusion.has(candidate)) labelOcclusion.set(candidate, surfaceOccludes(model, eye, candidate.point));
          return !labelOcclusion.get(candidate);
        });
        const anchor = visibleAnchor && visibleAnchor.candidate;
        if (!anchor) { label.hidden = true; return; }
        const point = transformPoint(combined, anchor.point);
        const inFront = point[3] > 0;
        const x = point[0] / point[3]; const y = point[1] / point[3];
        label.hidden = !inFront || Math.abs(x) > 0.98 || Math.abs(y) > 0.98;
        if (!label.hidden) { label.style.left = `${((x + 1) * 50).toFixed(2)}%`; label.style.top = `${((1 - y) * 50).toFixed(2)}%`; label.dataset.hemisphere = anchor.side || 'both'; }
      });
    };
    const updateSensorLabels = (matrix, rotationSettled) => {
      const labels = wrapper.querySelectorAll('[data-sensor-label]');
      if (!scalp || currentScalp < 0.85 || !rotationSettled) { labels.forEach((label) => { label.hidden = true; }); return; }
      const combined = multiply(viewProjection, matrix);
      const eye = [matrix[0] * camera[0] + matrix[1] * camera[1] + matrix[2] * camera[2], matrix[4] * camera[0] + matrix[5] * camera[1] + matrix[6] * camera[2], matrix[8] * camera[0] + matrix[9] * camera[1] + matrix[10] * camera[2]];
      labels.forEach((label) => {
        const name = label.dataset.sensorLabel;
        const active = sensorFocus === 'front' ? ['AF7', 'AF8'].includes(name) : sensorFocus === 'left-ear' ? name === 'TP9' : sensorFocus === 'right-ear' ? name === 'TP10' : sensorFocus === 'reference' ? name === 'FPz' : name === 'optical';
        label.dataset.active = String(active);
        const candidates = name === 'optical' ? [scalp.contacts.opticalLeft, scalp.contacts.opticalRight].filter(Boolean) : [scalp.contacts[name]].filter(Boolean);
        const contact = candidates.find((candidate) => {
          const normal = transformPoint(matrix, candidate.normal);
          return normal[2] > 0.08 && !surfaceOccludes(scalp, eye, candidate.point);
        });
        if (!contact) { label.hidden = true; return; }
        const point = transformPoint(combined, contact.point);
        const x = point[0] / point[3]; const y = point[1] / point[3];
        label.hidden = point[3] <= 0 || Math.abs(x) > 0.98 || Math.abs(y) > 0.98;
        if (!label.hidden) { label.style.left = `${((x + 1) * 50).toFixed(2)}%`; label.style.top = `${((1 - y) * 50).toFixed(2)}%`; }
      });
    };
    const updateProjectedBounds = (matrix, settled) => {
      if (!settled) return;
      const key = `${currentYaw.toFixed(3)}:${currentTilt.toFixed(3)}:${camera[1].toFixed(3)}:${camera[2].toFixed(3)}:${currentScalp.toFixed(2)}:${width}:${height}`;
      if (key === lastBoundsKey) return;
      lastBoundsKey = key;
      const combined = multiply(viewProjection, matrix);
      const meshes = [model];
      if (scalp && currentScalp > 0.01) {
        meshes.push(scalp);
        if (scalp.band) meshes.push(scalp.band);
        if (scalp.pod) meshes.push(scalp.pod);
        Object.values(scalp.contacts).forEach((contact) => meshes.push(contact.mesh));
      }
      let left = Infinity; let right = -Infinity; let top = Infinity; let bottom = -Infinity;
      meshes.forEach((mesh) => {
        for (let index = 0; index < mesh.positions.length; index += 3) {
          if (mesh === scalp && mesh.positions[index + 1] < mesh.clipY) continue;
          const point = transformPoint(combined, [mesh.positions[index], mesh.positions[index + 1], mesh.positions[index + 2]]);
          if (point[3] <= 0) continue;
          const x = (point[0] / point[3] + 1) / 2; const y = (1 - point[1] / point[3]) / 2;
          left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y);
        }
      });
      canvas.dataset.projectedBounds = JSON.stringify({ left: +left.toFixed(4), right: +right.toFixed(4), top: +top.toFixed(4), bottom: +bottom.toFixed(4) });
      canvas.dataset.projectedBoundsKind = 'all-active-source-vertices-relative-to-canvas';
    };
    const render = (time) => {
      frame = 0;
      if (disposed || lost || !visible || !gl || !model || document.hidden) return;
      try {
        resize(); updateTargets();
        const elapsed = lastTime ? Math.min(64, time - lastTime) : 16; lastTime = time;
        const amount = reduced.matches ? 1 : 1 - Math.exp(-elapsed / 155);
        currentYaw = mix(currentYaw, targetYaw, amount); currentTilt = mix(currentTilt, targetTilt, amount);
        currentRegions = currentRegions.map((value, index) => mix(value, targetRegions[index], amount));
        currentScalp = mix(currentScalp, targetScalp, amount);
        const matrix = rotation(currentYaw, currentTilt);
        const fitResidual = fitView(matrix, amount);
        const phase = currentScalp > 0.98 ? 'scalp' : currentScalp > 0.02 ? 'transition' : 'brain';
        wrapper.dataset.phase = phase; canvas.dataset.phase = phase;
        canvas.dataset.scalpOpacity = (currentScalp * 0.24).toFixed(3);
        canvas.dataset.scalpVisibility = currentScalp.toFixed(3);
        gl.bindFramebuffer(gl.FRAMEBUFFER, targets[0].framebuffer); gl.viewport(0, 0, bloomWidth, bloomHeight); gl.clearColor(0, 0, 0, 0); gl.depthMask(true); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        drawScene(matrix, true);
        gl.bindFramebuffer(gl.FRAMEBUFFER, targets[1].framebuffer); gl.clear(gl.COLOR_BUFFER_BIT); drawQuad(targets[0].texture, [2.9 / bloomWidth, 0], false);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, width, height); gl.depthMask(true); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        drawScene(matrix, false); drawQuad(targets[1].texture, [0, 2.9 / bloomHeight], true);
        if (gl.getError() !== gl.NO_ERROR) throw new Error('Brain rendering unavailable');
        canvas.dataset.angle = currentYaw.toFixed(4); canvas.dataset.triangleCount = String(model.indices.length / 3);
        const rotationSettled = Math.abs(currentYaw - targetYaw) + Math.abs(currentTilt - targetTilt) + fitResidual + Math.abs(currentScalp - targetScalp) < 0.004;
        updateLabels(matrix, rotationSettled);
        updateSensorLabels(matrix, rotationSettled);
        updateProjectedBounds(matrix, rotationSettled);
        if (wrapper.dataset.state !== 'ready') setState('ready');
        const unsettled = Math.abs(currentYaw - targetYaw) + Math.abs(currentTilt - targetTilt) + Math.abs(currentScalp - targetScalp) + fitResidual + currentRegions.reduce((sum, value, index) => sum + Math.abs(value - targetRegions[index]), 0);
        if (unsettled > 0.004) schedule(); else lastTime = 0;
      } catch (_) { fail(); }
    };
    const schedule = () => { if (!frame && visible && gl && model && !disposed && !lost && !document.hidden) frame = window.requestAnimationFrame(render); };
    const setup = () => {
      gl = canvas.getContext('webgl', { alpha: true, antialias: true, depth: true, powerPreference: 'low-power', premultipliedAlpha: false, preserveDrawingBuffer: false });
      if (!gl) throw new Error('WebGL unavailable');
      shader = program(vertexSource, fragmentSource, ['aPosition', 'aNormal', 'aRegion', 'aSulc', 'aPfc'], ['uModel', 'uViewProjection', 'uCamera', 'uRegions', 'uBloomPass', 'uMaterial', 'uOpacity', 'uAccent', 'uScalpCut']);
      quadShader = program(quadVertexSource, blurSource, ['aPoint'], ['uTexture', 'uDirection', 'uComposite']);
      uploadMesh(model);
      if (scalp) { uploadMesh(scalp); if (scalp.band) uploadMesh(scalp.band); if (scalp.pod) uploadMesh(scalp.pod); Object.values(scalp.contacts).forEach((contact) => uploadMesh(contact.mesh)); }
      quadBuffer = buffer(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]));
      width = 0; height = 0; resize(); updateTargets();
      currentYaw = targetYaw; currentTilt = targetTilt; currentRegions = targetRegions.slice();
      currentScalp = targetScalp;
      schedule();
    };
    const load = async () => {
      if (fetched || disposed) return;
      fetched = true; setState('loading');
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit', cache: 'force-cache' });
        if (!response.ok) throw new Error('Cortical model unavailable');
        model = prepareModel(await response.json());
        const scalpUrl = wrapper.dataset.scalpUrl;
        if (scalpUrl) {
          try {
            const scalpResponse = await fetch(scalpUrl, { signal: controller.signal, credentials: 'omit', cache: 'force-cache' });
            if (!scalpResponse.ok) throw new Error('Scalp model unavailable');
            scalp = prepareScalp(await scalpResponse.json(), model);
            wrapper.dataset.scalpState = 'ready';
            canvas.dataset.sensorCount = String(Object.keys(scalp.contacts).length);
            canvas.dataset.bandState = scalp.band ? 'ready' : 'unavailable';
          } catch (error) { if (error.name === 'AbortError') throw error; wrapper.dataset.scalpState = 'unavailable'; }
        }
        if (!disposed) setup();
      } catch (error) { if (!disposed && error.name !== 'AbortError') fail(); }
    };
    const onVisibility = () => { if (document.hidden) stop(); else schedule(); };
    const onChange = () => { if (gl && !lost) schedule(); };
    const onResize = () => schedule();
    const onLost = (event) => { event.preventDefault(); lost = true; fail(); };
    const onRestored = () => {
      if (disposed || restoreCount > 1) return;
      restoreCount += 1; lost = false; resources.buffers = []; resources.programs = []; targets = [];
      try { setup(); } catch (_) { fail(); }
    };
    const mutations = new MutationObserver(onChange);
    mutations.observe(root, { attributes: true, attributeFilter: ['style', 'data-ih-brain-region-state', 'data-ih-brain-step-state', 'data-ih-motion'] });
    const near = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { near.disconnect(); load(); } }, { rootMargin: '700px 0px', threshold: 0 }) : null;
    const observation = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) { load(); schedule(); } else stop();
    }, { rootMargin: '80px 0px', threshold: 0 }) : null;
    const resizing = 'ResizeObserver' in window ? new ResizeObserver(onResize) : null;
    if (near) near.observe(wrapper);
    if (observation) observation.observe(wrapper); else { visible = true; load(); }
    if (resizing) resizing.observe(canvas);
    reduced.addEventListener('change', onChange);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('resize', onResize, { passive: true });
    canvas.addEventListener('webglcontextlost', onLost, false); canvas.addEventListener('webglcontextrestored', onRestored, false);
    const destroy = () => {
      disposed = true; stop(); controller.abort(); mutations.disconnect();
      if (near) near.disconnect(); if (observation) observation.disconnect(); if (resizing) resizing.disconnect();
      reduced.removeEventListener('change', onChange); document.removeEventListener('visibilitychange', onVisibility); window.removeEventListener('resize', onResize);
      canvas.removeEventListener('webglcontextlost', onLost); canvas.removeEventListener('webglcontextrestored', onRestored);
      if (gl && !lost) { destroyTargets(); resources.buffers.forEach((item) => gl.deleteBuffer(item)); resources.programs.forEach((item) => gl.deleteProgram(item)); const extension = gl.getExtension('WEBGL_lose_context'); if (extension) extension.loseContext(); }
      instances.delete(wrapper);
    };
    instances.set(wrapper, destroy);
  };
  const initialiseWithin = (container) => {
    if (container.matches && container.matches('[data-ih-brain-3d]')) initialise(container);
    container.querySelectorAll('[data-ih-brain-3d]').forEach(initialise);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseWithin(document), { once: true }); else initialiseWithin(document);
  document.addEventListener('shopify:section:load', (event) => initialiseWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches && event.target.matches('[data-ih-brain-3d]')) { const destroy = instances.get(event.target); if (destroy) destroy(); }
    event.target.querySelectorAll('[data-ih-brain-3d]').forEach((wrapper) => { const destroy = instances.get(wrapper); if (destroy) destroy(); });
  });
})();
